import { createAuthThunks } from "@iotos/ims-auth-sdk/redux";
import {
  createAsyncThunk,
  createSlice,
  ThunkDispatch,
  UnknownAction,
} from "@reduxjs/toolkit";

import { API_ENDPOINT } from "../../config/api";
import { PATHS } from "../../constants";
import { IAppUsersDto } from "../../modules/app-users/shared/dtos/app-users";
import { clearFiltersAction } from "../../modules/filter/store/filters.slice";
import { setInfoData } from "../../modules/info-data/info-data-actions";
import { clearApiCache } from "../../modules/offline/shared/utils/clear-api-cache";
import { fetchWithAuth } from "../../shared/api/fetch-with-auth";
import type { RootState } from "../../store-types";
import { imsAuthService, SessionCheckError } from "../shared/ims-auth-service";
import { IAuthState, SessionCheckFailure } from "../shared/interfaces/auth-state";
import { getCurrentRelativeUrl } from "../shared/return-url";

import { AUTH_MODULE_NAME } from "./constants";

export const { signOut: imsSignOut, loadCurrentUser: getCurrentUser } = createAuthThunks({
  authService: imsAuthService,
});

type ImsSessionInfo = Awaited<ReturnType<typeof imsAuthService.initialize>>;

const withDefaultFarm = async (sessionInfo: ImsSessionInfo): Promise<ImsSessionInfo> => {
  if (!sessionInfo.isValid || !sessionInfo.user) {
    return sessionInfo;
  }
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/AppUsersV2/${sessionInfo.user.userId}`
  ).catch(() => null);
  if (!imsAuthService.getIsAuthenticated()) {
    return { isValid: false, user: null };
  }
  const profile: Partial<Pick<IAppUsersDto, "defaultFarmId">> = response?.ok
    ? await response.json().catch(() => ({}))
    : {};
  return {
    ...sessionInfo,
    user: { ...sessionInfo.user, defaultFarmId: profile.defaultFarmId },
  };
};

export const initializeAuth = createAsyncThunk<
  ImsSessionInfo,
  void,
  { rejectValue: { failure: SessionCheckFailure; detail: string } }
>(`${AUTH_MODULE_NAME}/initialize`, async (_, { rejectWithValue }) => {
  try {
    return await withDefaultFarm(await imsAuthService.initialize());
  } catch (error) {
    const isForbidden = error instanceof SessionCheckError && error.isForbidden;
    return rejectWithValue({
      failure: isForbidden ? "forbidden" : "error",
      detail: error instanceof Error ? error.message : "Не удалось проверить сессию",
    });
  }
});

export const initialState: IAuthState = {
  isAuthenticated: false,
  user: null,
  isLoadingUserDto: false,
  error: null,
  sessionCheckFailure: null,
};

export const authSlice = createSlice({
  name: AUTH_MODULE_NAME,
  initialState,
  reducers: {
    logoutSuccess: (state) => {
      state.isAuthenticated = false;
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoadingUserDto = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoadingUserDto = false;
        state.isAuthenticated = action.payload !== null;
        state.user = action.payload ?? null;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoadingUserDto = false;
        state.error = action.payload?.detail ?? "Не удалось загрузить пользователя";
        authSlice.caseReducers.logoutSuccess(state);
      })
      .addCase(initializeAuth.pending, (state) => {
        state.isLoadingUserDto = true;
        state.error = null;
        state.sessionCheckFailure = null;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoadingUserDto = false;
        state.isAuthenticated = action.payload.isValid;
        state.user = action.payload.isValid ? (action.payload.user ?? null) : null;
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.isLoadingUserDto = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload?.detail ?? "Не удалось проверить сессию";
        state.sessionCheckFailure = action.payload?.failure ?? "error";
      })
      .addCase(imsSignOut.pending, (state) => {
        state.isLoadingUserDto = true;
      })
      .addCase(imsSignOut.fulfilled, (state) => {
        state.isLoadingUserDto = false;
        state.isAuthenticated = false;
        state.user = null;
      })
      .addCase(imsSignOut.rejected, (state, action) => {
        state.isLoadingUserDto = false;
        state.error = action.payload?.detail ?? "Не удалось завершить сессию";
      });
  },
});

export const { logoutSuccess } = authSlice.actions;

export const authSliceReducer = authSlice.reducer;

const resetUserState = (
  dispatch: ThunkDispatch<RootState, unknown, UnknownAction>
): void => {
  dispatch(logoutSuccess());
  dispatch(clearFiltersAction());
  dispatch(setInfoData({ initialDictionariesLoaded: false }));
};

export const logout = createAsyncThunk<void, void, { state: RootState }>(
  `${AUTH_MODULE_NAME}/logout`,
  async (_, { dispatch }) => {
    resetUserState(dispatch);
    await clearApiCache();
    await imsAuthService.signOutTo(PATHS.SIGNED_OUT);
  }
);

export const sessionExpired = createAsyncThunk<void, void, { state: RootState }>(
  `${AUTH_MODULE_NAME}/sessionExpired`,
  async (_, { dispatch, getState }) => {
    if (!getState()[AUTH_MODULE_NAME].isAuthenticated) {
      return;
    }
    const returnUrl = encodeURIComponent(getCurrentRelativeUrl());
    resetUserState(dispatch);
    await clearApiCache();
    window.location.assign(`${PATHS.SESSION_EXPIRED}?returnUrl=${returnUrl}`);
  }
);
